// packages/main/app/web/components-v2/UnifiedImageDialog/AIImageChat.tsx

import * as Api from '@ViewModels';
import { useQueryClient } from '@tanstack/react-query';
import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { useEventLogging } from '../../../models/Logging';
import { useAIAssistantCreateConversationMutation } from '../../../queries/AI/useAIAssistantCreateConversationMutation';
import {
	getConversationChatMessageQueryKey,
	useAIConversationChatMessageQuery,
} from '../../../queries/AIConversation/useAIConversationChatMessageQuery';
import { useImageAttachmentMutation } from '../../../queries/Attachments/useImageAttachmentMutation';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import {
	AIChatAssistantMessage,
	removeErrorMessageFromConversationCache,
	useAIAssistantStreamingMutation,
} from '../../containers/templates/AssistantChat';
import { bs } from '../../styles/styles';
import { AIConversationMessage } from '../ai/AIConversationMessage';
import { ChatConversation, ChatConversationContent, ChatConversationScrollButton } from '../ai/ChatConversation';
import { ChatInput } from '../ai/ChatInput';
import { ImageAttachment } from '../ai/ImageAttachmentManager';
import { buildDraftContext } from './draftContext';
import { s } from './styles';
import { PostContentType } from './types';

const IMAGE_ASSISTANT_NAME = 'ImageGenerationAgent';

interface AIImageChatProps {
	initialImages?: Api.IFileAttachment[];
	existingConversationId?: string;
	onImageSelected?: (fileAttachments: Api.IFileAttachment[]) => void;
	onConversationCreated?: (conversationId: string) => void;
	impersonationContext?: Api.IImpersonationContext;
	assistantName?: string;
	showApplyBrandingButton?: boolean;
	postContent?: string;
	postContentType?: PostContentType;
	templateId?: string;
}

const useImageAssistantConversation = (
	isOpen: boolean,
	assistantName: string,
	impersonationContext?: Api.IImpersonationContext,
	existingConversationId?: string,
	onConversationCreated?: (conversationId: string) => void
) => {
	const logger = useEventLogging('useImageAssistantConversation');
	const aiAssistantCreateConversationMutation = useAIAssistantCreateConversationMutation({
		onError: error => {
			logger.logApiError(`useImageAssistantConversation-error`, error);
			toast.error('Failed to create conversation');
		},
		onSuccess: data => {
			if (data?.id) {
				onConversationCreated?.(data.id);
			}
		},
	});

	const aiConversationChatMessageQuery = useAIConversationChatMessageQuery({
		enabled: Boolean(existingConversationId || aiAssistantCreateConversationMutation.data),
		id: existingConversationId || aiAssistantCreateConversationMutation.data?.id,
		impersonationContext,
		staleTime: Infinity,
	});

	React.useEffect(() => {
		// Skip creating a new conversation if we already have one
		if (existingConversationId) {
			return;
		}

		if (isOpen && aiAssistantCreateConversationMutation.isIdle && !aiAssistantCreateConversationMutation.data) {
			aiAssistantCreateConversationMutation.mutate({
				assistantName,
				impersonationContext,
			});
		}
	}, [aiAssistantCreateConversationMutation, isOpen, impersonationContext, assistantName, existingConversationId]);

	return {
		conversationId: existingConversationId || aiAssistantCreateConversationMutation.data?.id,
		aiConversationChatMessageQuery,
		isLoadingConversation: !existingConversationId && aiAssistantCreateConversationMutation.isPending,
	};
};

export const AIImageChat = ({
	initialImages,
	existingConversationId,
	onImageSelected,
	onConversationCreated,
	impersonationContext,
	assistantName,
	showApplyBrandingButton,
	postContent,
	postContentType = 'social',
	templateId,
}: AIImageChatProps) => {
	const id = React.useId();
	const queryClient = useQueryClient();
	const logger = useEventLogging('AIImageChat');
	const effectiveAssistantName = assistantName || IMAGE_ASSISTANT_NAME;
	const [convertedInitialImages, setConvertedInitialImages] = React.useState<ImageAttachment[]>([]);

	const { conversationId, aiConversationChatMessageQuery, isLoadingConversation } = useImageAssistantConversation(
		true, // Always consider "open" since this is just the chat UI component
		effectiveAssistantName,
		impersonationContext,
		existingConversationId,
		onConversationCreated
	);

	// Convert IFileAttachment to ImageAttachment format
	React.useEffect(() => {
		if (!initialImages || initialImages.length === 0) {
			setConvertedInitialImages([]);
			return;
		}

		// Create ImageAttachment objects using the existing file attachment data
		// We create a minimal File object just to satisfy the interface, but we'll primarily use the existing attachment IDs
		const converted = initialImages.map((fileAttachment, index) => {
			// Create a minimal File object (won't be uploaded since we already have the attachment)
			const file = new File([], fileAttachment.fileName || `image-${index}`, {
				type: fileAttachment.mimeType || 'image/png',
			});

			return {
				id: fileAttachment.id,
				file,
				previewUrl: fileAttachment.url,
				name: fileAttachment.fileName || `image-${index}`,
				size: fileAttachment.fileSize || 0,
			} as ImageAttachment;
		});

		setConvertedInitialImages(converted);
	}, [initialImages]);

	const handleUseImage = React.useCallback(
		(fileAttachments: Api.IFileAttachment[]) => {
			onImageSelected?.(fileAttachments);
			toast.success('Image added');
		},
		[onImageSelected]
	);

	const aiAssistantStreamingMutation = useAIAssistantStreamingMutation({
		assistantName: effectiveAssistantName,
	});

	const fileAttachmentMutation = useImageAttachmentMutation({
		onError: error => {
			logger.logApiError('AIImageChat-upload-error', error);
			toast.error('Failed to upload images. Please try again.');
		},
	});

	const handleTryAgain = (chatRequest: Api.AIChatRequest) => {
		if (!conversationId) {
			return;
		}
		removeErrorMessageFromConversationCache(conversationId, impersonationContext);
		aiAssistantStreamingMutation.mutate({
			conversationId,
			chatRequest,
			impersonationContext,
		});
	};

	const handlePromptSubmit = React.useCallback(
		async (prompt: string, images?: ImageAttachment[]) => {
			if (!conversationId) {
				toast.error('Conversation not ready. Please try again.');
				return;
			}

			try {
				removeErrorMessageFromConversationCache(conversationId, impersonationContext);

				// Handle images: separate existing attachments from new uploads
				const fileAttachments: Api.IFileAttachment[] = [];
				if (images && images.length > 0) {
					// Separate images that already have IDs (from initialImages) from new uploads
					const existingAttachments = images.filter(img => img.id && img.file.size === 0);
					const newUploads = images.filter(img => !img.id || img.file.size > 0);

					// Use existing attachments directly
					if (existingAttachments.length > 0 && initialImages) {
						const existingFileAttachments = initialImages.filter(fa => existingAttachments.some(ea => ea.id === fa.id));
						fileAttachments.push(...existingFileAttachments);
					}

					// Upload new images
					if (newUploads.length > 0) {
						const uploadResult = await fileAttachmentMutation.mutateAsync({
							files: newUploads.map(img => img.file),
							impersonationContext,
						});
						fileAttachments.push(...uploadResult);
					}
				}

				// Add user message to cache (with fileAttachments for display purposes only)
				queryClient.setQueryData(
					getConversationChatMessageQueryKey({
						id: conversationId,
						impersonationContext,
					}),
					(prevMessages: Api.AIChatMessage[]) => [
						...(prevMessages ?? []),
						{
							role: Api.AIChatRole.User,
							text: prompt,
							id: null,
							fileAttachments: fileAttachments.length > 0 ? fileAttachments : undefined,
						},
					]
				);

				// Attaching the draft as context lets the image agent reference it directly, so users don't
				// have to copy-paste it into the prompt (the backend handler injects it as a system message).
				const contexts: Api.AIContext[] = [];
				if (fileAttachments.length > 0) {
					contexts.push({
						_type: 'MediaAIContext',
						fileAttachmentIds: fileAttachments.map(attachment => attachment.id),
					} as Api.MediaAIContext);
				}
				if (postContent) {
					contexts.push(buildDraftContext(postContent, postContentType));
				}

				const chatRequest: Api.AIMessageRequest = {
					chatMessages: [
						{
							role: Api.AIChatRole.User,
							text: prompt,
							id: null,
						},
					],
					contexts: contexts.length > 0 ? contexts : undefined,
				};

				aiAssistantStreamingMutation.mutate({
					conversationId,
					chatRequest,
					impersonationContext,
				});
			} catch (error) {
				logger.logApiError('AIImageChat-submit-error', error as Api.IOperationResultNoValue);
				toast.error('Failed to send message. Please try again.');
			}
		},
		[
			conversationId,
			impersonationContext,
			initialImages,
			fileAttachmentMutation,
			queryClient,
			aiAssistantStreamingMutation,
			logger,
			postContent,
			postContentType,
		]
	);

	const handleApplyBranding = React.useCallback(
		async (images: ImageAttachment[]) => {
			const existingAttachments = images.filter(img => img.id && img.file.size === 0);
			const attachmentIds = existingAttachments.map(img => img.id);

			const action = {
				_type: 'ImageBrandingAction',
				sourceFileAttachmentIds: attachmentIds,
				...(templateId ? { templateId } : {}),
			} as Api.ImageBrandingAction;
			const chatRequest: Api.AIActionRequest = { _type: 'AIActionRequest', action };

			aiAssistantStreamingMutation.mutate({
				conversationId,
				chatRequest,
				impersonationContext,
			});
		},
		[aiAssistantStreamingMutation, conversationId, impersonationContext, templateId]
	);

	if (isLoadingConversation || !conversationId) {
		return (
			<div className={css(bs.flex, bs.itemsCenter, bs.justifyCenter, bs.hFull)}>
				<LoadingSpinner />
			</div>
		);
	}

	return (
		<div className={css(s.chatContainer)}>
			<ChatConversation styles={[s.chatMessages]}>
				<ChatConversationContent>
					<AIChatAssistantMessage>
						Hey! I can help you generate or modify images. Upload an image and tell me what you&apos;d like to do with
						it, or just describe an image you&apos;d like me to create.
					</AIChatAssistantMessage>
					{aiConversationChatMessageQuery.data?.map((message, i) => {
						return (
							<AIConversationMessage
								key={`${id}-assistant-message-${message.id ?? i}`}
								message={message}
								onUseImage={onImageSelected ? handleUseImage : undefined}
								onImageGenJobComplete={() => aiConversationChatMessageQuery.refetch()}
								onErrorTryAgain={handleTryAgain}
								conversationId={conversationId}
								impersonationContext={impersonationContext}
								showMessageToolbar={false}
							/>
						);
					})}
					{aiAssistantStreamingMutation.isLoading && (
						<AIChatAssistantMessage>
							<LoadingSpinner type='tiny' />
						</AIChatAssistantMessage>
					)}
				</ChatConversationContent>

				<ChatConversationScrollButton />
			</ChatConversation>

			<div className={css(s.chatInputContainer)}>
				<ChatInput
					streamingInProgress={aiAssistantStreamingMutation.isLoading || aiAssistantStreamingMutation.isStreaming}
					placeholder='Describe the image you want...'
					onSubmit={handlePromptSubmit}
					supportsImages={true}
					maxImages={2}
					initialImages={convertedInitialImages}
					onApplyBrandingClick={showApplyBrandingButton ? handleApplyBranding : undefined}
				/>
			</div>
		</div>
	);
};
